import {Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {EMPTY, merge, Observable} from 'rxjs';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {debounceTime, map, shareReplay, switchMap, switchMapTo, take, tap} from 'rxjs/operators';
import {SelectionModel} from '@angular/cdk/collections';
import {PageEvent} from '@angular/material/paginator';
import {Sort} from '@angular/material/sort';

import {RecipientContactsDataSource} from './recipientContactsDataSource';
import {RecipientContactsTableService} from './recipient-contacts-table.service';
import {GpRecipientContactsDataService} from '../../../services/gp/gp-recipient-contacts-data.service';
import {RecipientReadDto} from '../../models/recipient-read-dto.model';
import {PermissionService} from 'src/app/permission/permission.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {UserService} from 'src/app/services/shared/user.service';
import {PaymentType} from '../../enums/payment-type.enum';

@Component({
  selector: 'terra-recipient-contacts-display-table',
  templateUrl: './recipient-contacts-display-table.component.html',
  styleUrls: ['./recipient-contacts-display-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0', visibility: 'hidden', opacity: 0})),
      state('expanded', style({height: '*', visibility: 'visible', opacity: 1})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecipientContactsDisplayTableComponent extends OnDestroyMixin implements OnInit {
  allowInvestorName$ = this.permissionService.allowInvestorName$;
  isAllContactsVisible$ = this.permissionService.isAllContactsVisible$;


  @Input() showMessageWhenListIsEmpty = true;
  @Input() selectMany = true;

  @Output() editRow: EventEmitter<number> = new EventEmitter();
  @Output() deleteRow: EventEmitter<number[]> = new EventEmitter();
  @Output() viewTransactionHistory: EventEmitter<RecipientReadDto> = new EventEmitter();

  contactPerPageOptions = [25, 50, 100, 200, 500];
  perPageControl = new UntypedFormControl(this.contactPerPageOptions[0]);
  perPageChanged$: Observable<number> = this.perPageControl.valueChanges.pipe(untilComponentDestroyed(this), debounceTime(200));

  dataSource = new RecipientContactsDataSource(this.recipientContactsTableService, this.recipientContactsDataService);
  searchOptions$ = this.recipientContactsTableService.searchOptions$;

  pageRowsCount$ = this.dataSource.pageRows$.pipe(map(rows => rows ? rows.length : 0), shareReplay(1));
  totalRowsCount$ = this.dataSource.totalRowsCount$;

  isLoading$ = this.dataSource.isLoading$;

  isGp$ = this.userService.accountDetails$.pipe(
    map(accountDetails => accountDetails.isAgent),
    shareReplay(1)
  );

  selection = new SelectionModel<RecipientReadDto>(this.selectMany, []);
  displayedColumns: string[] = ['select', 'name', 'routingNumber', 'accountNumber', 'accountType', 'tags', 'action', 'expand'];
  displayedColumnsLp: string[] = ['select', 'name', 'routingNumber', 'accountNumber', 'accountType', 'action', 'expand'];

  displayedColumns$ = this.isGp$.pipe(
    map(isGp => isGp ? this.displayedColumns : this.displayedColumnsLp)
  );

  expandedElement: number;

  isAllSelected$ = merge(this.selection.changed, this.pageRowsCount$).pipe(
    switchMapTo(this.pageRowsCount$),
    map(pageRowsCount => pageRowsCount === this.selection.selected.length),
    shareReplay(1));

  CrePaymentType = PaymentType;

  constructor(private recipientContactsTableService: RecipientContactsTableService,
              private recipientContactsDataService: GpRecipientContactsDataService,
              private permissionService: PermissionService,
              private userService: UserService) {
    super();
  }

  ngOnInit(): void {
    this.recipientContactsTableService.updatePageSize(this.contactPerPageOptions[0]);

    this.perPageChanged$.pipe(untilComponentDestroyed(this)).subscribe(
      pageSize => {
        this.recipientContactsTableService.updatePageSize(pageSize);
      });

    this.dataSource.pageRows$.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.clearSelection();
    });
  }

  get selectedRecipientContacts() {
    if (!this.selection) {
      return [];
    }
    return this.selection.selected;
  }

  trackByFunction(index: number, item: RecipientReadDto) {
    return item.id;
  }

  pageChange(event: PageEvent) {
    this.recipientContactsTableService.updatePageNumber(event.pageIndex);
  }

  rowCheckToggle(row: RecipientReadDto, unselectOthers = !this.selectMany, event = null) {
    if (event && (event.ctrlKey || event.metaKey)) {
      unselectOthers = false;
    }

    const isRowChecked = this.isRowChecked(row);
    const currentNumberOfSelectedItems = this.selectedRecipientContacts.length;

    if (unselectOthers) {
      this.clearSelection();
    }

    if (isRowChecked && (currentNumberOfSelectedItems === 1 || !unselectOthers)) {
      this.selection.deselect(row);
    } else {
      this.selection.select(row);
    }
  }

  isRowChecked(row: RecipientReadDto) {
    return this.selection.selected.some(x => x.id === row.id);
  }

  masterToggle() {
    this.isAllSelected$.pipe(
      take(1),
      switchMap(isAllSelected => {
        if (isAllSelected) {
          this.selection.clear();
          return EMPTY;
        } else {
          return this.dataSource.pageRows$;
        }
      }),
      tap(rowsToSelect => {
        this.selection.select(...rowsToSelect);
      }),
      take(1)
    ).subscribe();
  }

  // Check if all rows are selected
  deleteMulitple() {
    if (this.selection.selected.length === 0) {
      return;
    }

    const ids = this.selection.selected.map(i => i.id);

    this.deleteRow.emit(ids);
  }

  onEditRow(id: number) {
    if (!this.editRow) {
      return;
    }
    this.editRow.emit(id);
  }

  onDeleteRow(id: number) {
    if (!this.deleteRow) {
      return;
    }
    this.deleteRow.emit([id]);
  }

  onViewTransactionHistory(recipient: RecipientReadDto) {
    if (!this.viewTransactionHistory) {
      return;
    }
    this.viewTransactionHistory.emit(recipient);
  }

  showActions(model: RecipientReadDto) {
    if (this.selection.selected.length === 1) {
      return model.id === this.selection.selected[0].id;
    }

    return model.hover;
  }

  sortData(sort: Sort) {
    if (!sort.active || !sort.direction) {
      return;
    }
    this.recipientContactsTableService.sort(sort.active, sort.direction);
  }

  private clearSelection() {
    this.selection.clear();
  }
}
